/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, HeartHandshake, Sparkles, Search, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import Logo from '@/components/Logo';
import NotificationsBell from '@/components/NotificationsBell';
import SeniorModeToggle from '@/components/a11y/SeniorModeToggle';
import OnboardingCTA from '@/components/OnboardingCTA';
import StripeConnectCTA from '@/components/StripeConnectCTA';
import SearchDialog from '@/components/SearchDialog';
import { useAuth } from '@/lib/auth';
import { isPatientUser } from './navItems';
import {
  LanguageSwitcher, ThemeToggle, OverflowThemeItem, OverflowSeniorModeItem, OverflowLanguageItem,
} from './TopBarControls';
import { UserMenu } from './UserMenu';

/**
 * TopBar — sticky header: logo, quick actions (desktop), overflow menu (mobile),
 * notifications and user menu. Extracted from AppShell.jsx (Code Quality refactor).
 */

const ICON_BTN = 'h-9 w-9 rounded-full hover:bg-secondary flex items-center justify-center transition';

/** Cmd/Ctrl+K or "/" opens global search (ignored while typing in a field). */
const useSearchShortcut = (open) => {
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      return el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable;
    };
    const onKey = (e) => {
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      const slash = e.key === '/' && !isTyping();
      if (cmdK || slash) { e.preventDefault(); open(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
};

/** Quick actions shared by the desktop bar and the mobile overflow menu. */
const useQuickActions = (isPatient, t, navigate, openSearch) => [
  { key: 'search', Icon: Search, label: t('searchTitle'), onClick: openSearch, testId: 'search', iconCls: '' },
  { key: 'ai', Icon: Sparkles, label: isPatient ? t('aiPatientNavLabel') : t('aiAssistant'), onClick: () => navigate(isPatient ? '/ai-patient' : '/ai'), testId: 'ai', iconCls: 'text-primary' },
  { key: 'wellness', Icon: HeartHandshake, label: t('wellness'), onClick: () => navigate('/wellness'), testId: 'wellness', hidden: isPatient },
  { key: 'resources', Icon: BookOpen, label: t('resources'), onClick: () => navigate('/resources'), testId: 'resources', hidden: isPatient },
].filter((a) => !a.hidden);

export const TopBar = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const isPatient = isPatientUser(user);
  const openSearch = React.useCallback(() => setSearchOpen(true), []);
  useSearchShortcut(openSearch);

  const actions = useQuickActions(isPatient, t, navigate, openSearch);
  // Desktop order: AI, wellness, resources, search (search last, next to a11y/theme/lang)
  const desktopActions = [...actions.filter((a) => a.key !== 'search'), actions.find((a) => a.key === 'search')];

  return (
    <>
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border overflow-x-clip">
        <div className="max-w-[1100px] mx-auto flex items-center justify-between gap-2 px-3 sm:px-4 h-14">
          <div className="flex items-center gap-2 cursor-pointer min-w-0 shrink" onClick={() => navigate(isPatient ? '/telemedicine' : '/feed')} data-testid="topbar-logo">
            <Logo size={34} withText textClassName="text-sm" className="min-w-0" />
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {/* Onboarding CTA — visible only for professionals with incomplete onboarding */}
            <div className="mr-1"><OnboardingCTA /></div>
            <div className="mr-1"><StripeConnectCTA /></div>

            {/* Secondary actions — desktop only (≥lg); collapsed into overflow menu below */}
            <div className="hidden lg:flex items-center gap-0.5">
              {desktopActions.map(({ key, Icon, label, onClick, testId, iconCls }) => (
                <button key={key} onClick={onClick} className={`${ICON_BTN} ${iconCls || ''}`} title={label} aria-label={label} data-testid={`topbar-${testId}`}>
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </button>
              ))}
              <SeniorModeToggle compact />
              <ThemeToggle />
              <LanguageSwitcher />
            </div>

            {/* Overflow menu — smaller viewports (<lg) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`lg:hidden ${ICON_BTN}`} title={t('menuMore') || 'Altro'} aria-label={t('menuMore') || 'Altro'} data-testid="topbar-overflow-menu">
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {actions.map(({ key, Icon, label, onClick, testId, iconCls }) => (
                  <DropdownMenuItem key={key} onClick={onClick} data-testid={`overflow-${testId}`}>
                    <Icon className={`h-4 w-4 mr-2 ${iconCls || ''}`} /> {label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <OverflowThemeItem />
                <OverflowSeniorModeItem />
                <OverflowLanguageItem />
              </DropdownMenuContent>
            </DropdownMenu>

            <NotificationsBell />
            <UserMenu />
          </div>
        </div>
      </header>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};
